import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, Trash2, Send, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';

const ROLE_LABEL = {
  admin: 'Admin · todo el país',
  territory_manager: 'TM · estaciones de su zona',
  agent: 'Agente · sus estaciones',
  capturer: 'Capturador',
};

export default function AdminDailyReport() {
  const [recs, setRecs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [brand, setBrand] = useState('');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get('/compliance-report/recipients'),
        api.get('/users'),
      ]);
      setRecs(r1.data || []);
      setUsers((r2.data || []).filter(x => x.email && x.status === 'active'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selUser = users.find(x => x.id === userId);
  const brandApplies = selUser && !['territory_manager', 'agent'].includes(selUser.role);

  const add = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      await api.post('/compliance-report/recipients', { user_id: userId, brand: brandApplies ? (brand || null) : null });
      toast.success('Destinatario agregado');
      setUserId(''); setBrand('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/compliance-report/recipients/${id}`);
    setRecs(r => r.filter(x => x.id !== id));
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post('/compliance-report/send-now');
      toast.success(`Enviado a ${r.data.sent} de ${r.data.recipients} destinatario(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al enviar'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="max-w-3xl" data-testid="daily-report-page">
      <h1 className="font-serif text-[30px] tracking-tight mb-1">Reporte diario por correo</h1>
      <p className="text-[13px] text-[#5a5a5a] mb-6">
        Todos los días a las <strong>8:00 p.m. (GT)</strong> se envía el resumen de cumplimiento de trackings del día.
        Cada destinatario recibe solo su alcance: TM → su zona, agente → sus estaciones, admin/gerente → todo el país o su marca.
      </p>

      <div className="bg-white border border-[#e8e5de] rounded-xl p-5 mb-5">
        <p className="text-[11.5px] uppercase tracking-wider text-[#8a8579] mb-2">Agregar destinatario</p>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={userId} onChange={e => setUserId(e.target.value)}
            className="h-10 px-3 rounded-lg border border-[#e8e5de] bg-[#fafaf6] text-[13px] min-w-[260px]"
            data-testid="report-user-select">
            <option value="">Selecciona un usuario…</option>
            {users.map(x => <option key={x.id} value={x.id}>{x.name} · {ROLE_LABEL[x.role] || x.role}</option>)}
          </select>
          {brandApplies && (
            <select value={brand} onChange={e => setBrand(e.target.value)}
              className="h-10 px-3 rounded-lg border border-[#e8e5de] bg-[#fafaf6] text-[13px]"
              data-testid="report-brand-select">
              <option value="">Todo el país</option>
              <option value="UNO">Solo UNO</option>
              <option value="Shell">Solo Shell</option>
            </select>
          )}
          <Button onClick={add} disabled={!userId || busy} className="h-10 rounded-lg bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="report-add-btn">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14} className="mr-1"/>}Agregar
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#f0ede5]">
          <p className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Destinatarios ({recs.length})</p>
          <Button variant="outline" onClick={sendNow} disabled={sending || recs.length === 0} className="h-8 rounded-full text-[12px]" data-testid="report-send-now">
            {sending ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <Send size={13} className="mr-1.5"/>}Enviar prueba ahora
          </Button>
        </div>
        {recs.length === 0 ? (
          <p className="text-[13px] text-[#8a8579] p-6 text-center">Sin destinatarios aún — agrega a May, Juan, Carlos y los TMs.</p>
        ) : recs.map(r => (
          <div key={r.id} className="flex items-center gap-3 px-5 py-3 border-b border-[#f0ede5] last:border-0" data-testid={`report-recipient-${r.id}`}>
            <Mail size={14} className="text-[#8a8579] shrink-0"/>
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-medium truncate">{r.name} <span className="text-[#8a8579] font-normal">· {r.email}</span></p>
              <p className="text-[11px] text-[#8a8579]">{ROLE_LABEL[r.role] || r.role}{r.brand ? ` · solo ${r.brand}` : ''}</p>
            </div>
            <button onClick={() => remove(r.id)} className="text-[#b91c1c] p-1.5 rounded hover:bg-[#fee2e2]" data-testid={`report-remove-${r.id}`}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
